import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_NAME = 'The Gardeners Calendar';
export const SITE_URL = 'https://www.the-gardeners-calendar.co.uk';

/** Finds a head tag by attribute, creating it if it isn't there, then sets its content. */
function upsertMeta(key: 'name' | 'property', value: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

/**
 * Sets the title, meta description and canonical URL for the current route.
 *
 * Every page component should call this. Without it each route inherits the one
 * static <title> in index.html, which leaves every page on the site looking
 * identical to search engines — and reports as a single row in Analytics.
 *
 * This runs client-side. Google renders JavaScript so it reads the final values,
 * but social scrapers (Facebook, X) do not — share cards still come from the
 * static Open Graph tags in index.html.
 */
/**
 * `image` must be at least 200x200, and ideally 1200x630 — Facebook silently
 * drops anything smaller and renders a card with no image, which is worse than
 * the generic one. index.html declares og:image:width/height as 1200x630, so
 * any override should match those dimensions or update them too.
 */
export function usePageMeta(title: string, description: string, image?: string) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
    const url = `${SITE_URL}${pathname}`;
    // Scrapers need an absolute URL, and only follow the one in the raw HTML —
    // which is why the build prerenders these pages.
    const imageUrl = image
      ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
      : `${SITE_URL}/images/tgc-social.jpg`;

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertCanonical(url);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);
  }, [title, description, image, pathname]);
}
